import React, { useState, useEffect, useCallback } from "react";
import { useAuth } from "../context/AuthContext";
import { useToast } from "../context/ToastContext";
import Navigation from "../components/Navigation";
import { apiFetch } from "../utils/api";

interface Verification {
  id: number;
  ghana_card_number: string;
  date_of_birth: string;
  verification_result: any;
  image_url?: string;
  created_at: string;
  link_token?: string;
  verification_type?: string;
  creator_id?: number;
  creator_email?: string;
}

const Verifications: React.FC = () => {
  const { user, logout } = useAuth();
  const { showToast } = useToast();
  const [verifications, setVerifications] = useState<Verification[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedVerification, setSelectedVerification] =
    useState<Verification | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState<
    "all" | "verified" | "failed"
  >("all");
  const [filterType, setFilterType] = useState<"all" | "kyc" | "yesno">("all");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const fetchVerifications = useCallback(async () => {
    try {
      setLoading(true);
      const response = await apiFetch("/verifications");

      if (!response.ok) throw new Error("Failed to fetch verifications");

      const data = await response.json();
      setVerifications(data.verifications);
    } catch (err: any) {
      if (err.message !== "Session expired. Please log in again.") {
        showToast(err.message, "error");
      }
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    fetchVerifications();
  }, [fetchVerifications]);

  const getVerificationStatus = (result: any) => {
    if (!result) return { status: "Unknown", color: "inactive" };
    if (result.verified === true || result.verified === "TRUE") {
      return { status: "Verified", color: "active" };
    }
    return { status: "Failed", color: "inactive" };
  };

  const handleViewDetails = (verification: Verification) => {
    setSelectedVerification(verification);
    setShowModal(true);
  };

  const handlePrint = () => {
    window.print();
  };

  const getPersonData = (result: any) => {
    if (!result) return null;
    // Handle both direct person and nested data.person
    return result.person || result.data?.person || null;
  };

  const getTransactionData = (result: any) => {
    if (!result) return null;
    return result.data || result;
  };

  if (loading) {
    return (
      <div className="dashboard-container">
        <Navigation onLogout={logout} />
        <div className="dashboard-content">
          <div className="header">
            <h1 className="title">Loading...</h1>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-container">
      <Navigation onLogout={logout} />
      <div className="dashboard-content">
        <div className="header">
          <h1 className="title">Verifications</h1>
          <p className="subtitle">
            {user?.role === "admin"
              ? "View all verification records"
              : "View verifications from your links"}
          </p>
        </div>

        <div className="dashboard-section">
          <h2>Verification Records</h2>

          {/* Search and Filter Controls */}
          <div
            style={{
              marginBottom: "24px",
              display: "flex",
              gap: "12px",
              flexWrap: "wrap",
              alignItems: "center",
            }}
          >
            <input
              type="text"
              placeholder="Search by Ghana Card, creator email..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
              className="input"
              style={{ flex: "1", minWidth: "200px", maxWidth: "400px" }}
            />
            <select
              value={filterStatus}
              onChange={(e) => {
                setFilterStatus(
                  e.target.value as "all" | "verified" | "failed"
                );
                setCurrentPage(1);
              }}
              className="input"
              style={{ width: "auto", padding: "8px 12px" }}
            >
              <option value="all">All Status</option>
              <option value="verified">Verified</option>
              <option value="failed">Failed</option>
            </select>
            <select
              value={filterType}
              onChange={(e) => {
                setFilterType(e.target.value as "all" | "kyc" | "yesno");
                setCurrentPage(1);
              }}
              className="input"
              style={{ width: "auto", padding: "8px 12px" }}
            >
              <option value="all">All Types</option>
              <option value="kyc">KYC</option>
              <option value="yesno">YES/NO</option>
            </select>
          </div>

          {(() => {
            // Filter verifications
            const filteredVerifications = verifications.filter(
              (verification) => {
                const status = getVerificationStatus(
                  verification.verification_result
                );

                const matchesSearch =
                  searchTerm === "" ||
                  verification.ghana_card_number
                    .toLowerCase()
                    .includes(searchTerm.toLowerCase()) ||
                  verification.date_of_birth
                    .toLowerCase()
                    .includes(searchTerm.toLowerCase()) ||
                  (verification.creator_email &&
                    verification.creator_email
                      .toLowerCase()
                      .includes(searchTerm.toLowerCase()));

                const matchesStatus =
                  filterStatus === "all" ||
                  (filterStatus === "verified" &&
                    status.status === "Verified") ||
                  (filterStatus === "failed" && status.status === "Failed");

                const matchesType =
                  filterType === "all" ||
                  (filterType === "kyc" &&
                    verification.verification_type !== "yesno") ||
                  (filterType === "yesno" &&
                    verification.verification_type === "yesno");

                return matchesSearch && matchesStatus && matchesType;
              }
            );

            // Pagination
            const totalPages = Math.ceil(
              filteredVerifications.length / itemsPerPage
            );
            const startIndex = (currentPage - 1) * itemsPerPage;
            const paginatedVerifications = filteredVerifications.slice(
              startIndex,
              startIndex + itemsPerPage
            );

            return (
              <>
                {filteredVerifications.length === 0 ? (
                  <div className="empty-state">
                    <p>
                      {verifications.length === 0
                        ? "No verifications found."
                        : "No verifications match your search criteria."}
                    </p>
                  </div>
                ) : (
                  <>
                    <div
                      style={{
                        marginBottom: "16px",
                        color: "#666",
                        fontSize: "0.9rem",
                      }}
                    >
                      Showing {startIndex + 1}-
                      {Math.min(
                        startIndex + itemsPerPage,
                        filteredVerifications.length
                      )}{" "}
                      of {filteredVerifications.length} verifications
                    </div>
                    <div>
                      {paginatedVerifications.map((verification) => {
                        const status = getVerificationStatus(
                          verification.verification_result
                        );
                        return (
                          <div key={verification.id} className="link-card">
                            <div className="link-header">
                              <div>
                                <div
                                  style={{
                                    fontWeight: 600,
                                    marginBottom: "8px",
                                  }}
                                >
                                  Ghana Card: {verification.ghana_card_number}
                                </div>
                                <div
                                  className={`link-status ${status.color}`}
                                  style={{
                                    display: "inline-block",
                                    marginRight: "8px",
                                  }}
                                >
                                  {status.status}
                                </div>
                                {verification.verification_type && (
                                  <span
                                    className="link-status inactive"
                                    style={{ display: "inline-block" }}
                                  >
                                    {verification.verification_type.toUpperCase()}
                                  </span>
                                )}
                              </div>
                              <div style={{ display: "flex", gap: "8px" }}>
                                {verification.verification_result &&
                                  getPersonData(
                                    verification.verification_result
                                  ) && (
                                    <button
                                      className="link-copy-btn"
                                      onClick={() =>
                                        handleViewDetails(verification)
                                      }
                                      style={{
                                        background: "#004a9c",
                                        padding: "8px 12px",
                                      }}
                                      title="View Details"
                                    >
                                      View
                                    </button>
                                  )}
                              </div>
                            </div>
                            <div className="link-meta">
                              <div className="link-meta-item">
                                <span className="link-meta-label">
                                  Date of Birth
                                </span>
                                <span>{verification.date_of_birth}</span>
                              </div>
                              <div className="link-meta-item">
                                <span className="link-meta-label">
                                  Verified At
                                </span>
                                <span>
                                  {new Date(
                                    verification.created_at
                                  ).toLocaleString()}
                                </span>
                              </div>
                              {verification.link_token && (
                                <div className="link-meta-item">
                                  <span className="link-meta-label">
                                    Link Token
                                  </span>
                                  <span
                                    style={{
                                      fontFamily: "monospace",
                                      fontSize: "0.85rem",
                                    }}
                                  >
                                    {verification.link_token.substring(0, 16)}
                                    ...
                                  </span>
                                </div>
                              )}
                              {user?.role === "admin" &&
                                verification.creator_email && (
                                  <div className="link-meta-item">
                                    <span className="link-meta-label">
                                      Link Created By
                                    </span>
                                    <span>{verification.creator_email}</span>
                                  </div>
                                )}
                              {verification.verification_result && (
                                <>
                                  <div className="link-meta-item">
                                    <span className="link-meta-label">
                                      Code
                                    </span>
                                    <span>
                                      {verification.verification_result.code ||
                                        "N/A"}
                                    </span>
                                  </div>
                                  <div className="link-meta-item">
                                    <span className="link-meta-label">
                                      Message
                                    </span>
                                    <span>
                                      {verification.verification_result
                                        .message || "N/A"}
                                    </span>
                                  </div>
                                  {verification.verification_result
                                    .transactionGuid && (
                                    <div className="link-meta-item">
                                      <span className="link-meta-label">
                                        Transaction GUID
                                      </span>
                                      <span
                                        style={{
                                          fontFamily: "monospace",
                                          fontSize: "0.85rem",
                                        }}
                                      >
                                        {
                                          verification.verification_result
                                            .transactionGuid
                                        }
                                      </span>
                                    </div>
                                  )}
                                </>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    {totalPages > 1 && (
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          gap: "12px",
                          marginTop: "24px",
                          flexWrap: "wrap",
                        }}
                      >
                        <button
                          className="verify-button"
                          onClick={() =>
                            setCurrentPage((prev) => Math.max(1, prev - 1))
                          }
                          disabled={currentPage === 1}
                          style={{
                            background: currentPage === 1 ? "#ccc" : "#004a9c",
                            cursor:
                              currentPage === 1 ? "not-allowed" : "pointer",
                            width: "auto",
                            padding: "8px 16px",
                          }}
                        >
                          Previous
                        </button>
                        <span style={{ fontSize: "0.9rem", color: "#666" }}>
                          Page {currentPage} of {totalPages}
                        </span>
                        <button
                          className="verify-button"
                          onClick={() =>
                            setCurrentPage((prev) =>
                              Math.min(totalPages, prev + 1)
                            )
                          }
                          disabled={currentPage === totalPages}
                          style={{
                            background:
                              currentPage === totalPages ? "#ccc" : "#004a9c",
                            cursor:
                              currentPage === totalPages
                                ? "not-allowed"
                                : "pointer",
                            width: "auto",
                            padding: "8px 16px",
                          }}
                        >
                          Next
                        </button>
                      </div>
                    )}
                  </>
                )}
              </>
            );
          })()}
        </div>
      </div>

      {/* Modal for viewing person details */}
      {showModal && selectedVerification && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Verification Details</h2>
              <div style={{ display: "flex", gap: "12px" }}>
                <button
                  className="verify-button"
                  onClick={handlePrint}
                  style={{
                    background: "#004a9c",
                    width: "auto",
                    padding: "10px 20px",
                  }}
                >
                  Print
                </button>
                <button
                  className="verify-button"
                  onClick={() => setShowModal(false)}
                  style={{
                    background: "#000000",
                    width: "auto",
                    padding: "10px 20px",
                  }}
                >
                  Close
                </button>
              </div>
            </div>
            <div className="modal-body print-content">
              {(() => {
                const person = getPersonData(
                  selectedVerification.verification_result
                );
                const transactionData = getTransactionData(
                  selectedVerification.verification_result
                );

                if (!person) {
                  return <div>No person data available</div>;
                }

                return (
                  <div>
                    {/* Person Image */}
                    {selectedVerification.image_url && (
                      <div className="detail-section print-show print-photo-section">
                        <h3>Person Photo</h3>
                        <div
                          style={{
                            textAlign: "center",
                            marginBottom: "24px",
                            display: "flex",
                            flexDirection: "column",
                            gap: "16px",
                            alignItems: "center",
                          }}
                        >
                          <img
                            src={selectedVerification.image_url}
                            alt="Person Face Photo"
                            style={{
                              maxWidth: "300px",
                              maxHeight: "400px",
                              border: "2px solid #e9ecef",
                              borderRadius: "8px",
                              boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                            }}
                          />
                        </div>
                      </div>
                    )}

                    <div className="detail-section print-show print-personal-section">
                      <h3>Personal Information</h3>
                      <div className="detail-grid">
                        {person.cardId && (
                          <div className="detail-item">
                            <strong>Card ID:</strong> {person.cardId}
                          </div>
                        )}
                        {person.nationalId && (
                          <div className="detail-item">
                            <strong>National ID:</strong> {person.nationalId}
                          </div>
                        )}
                        {person.surname && (
                          <div className="detail-item">
                            <strong>Surname:</strong> {person.surname}
                          </div>
                        )}
                        {person.forenames && (
                          <div className="detail-item">
                            <strong>Forenames:</strong> {person.forenames}
                          </div>
                        )}
                        {person.gender && (
                          <div className="detail-item">
                            <strong>Gender:</strong> {person.gender}
                          </div>
                        )}
                        {person.birthDate && (
                          <div className="detail-item">
                            <strong>Birth Date:</strong> {person.birthDate}
                          </div>
                        )}
                        {person.birthTown && (
                          <div className="detail-item">
                            <strong>Birth Town:</strong> {person.birthTown}
                          </div>
                        )}
                        {person.birthRegion && (
                          <div className="detail-item">
                            <strong>Birth Region:</strong> {person.birthRegion}
                          </div>
                        )}
                        {person.birthDistrict && (
                          <div className="detail-item">
                            <strong>Birth District:</strong>{" "}
                            {person.birthDistrict}
                          </div>
                        )}
                        {person.birthCountry && (
                          <div className="detail-item">
                            <strong>Birth Country:</strong>{" "}
                            {person.birthCountry}
                          </div>
                        )}
                        {person.nationality && (
                          <div className="detail-item">
                            <strong>Nationality:</strong> {person.nationality}
                          </div>
                        )}
                        {person.cardValidFrom && (
                          <div className="detail-item">
                            <strong>Card Valid From:</strong>{" "}
                            {person.cardValidFrom}
                          </div>
                        )}
                        {person.cardValidTo && (
                          <div className="detail-item">
                            <strong>Card Valid To:</strong> {person.cardValidTo}
                          </div>
                        )}
                      </div>
                    </div>

                    {person.contact && (
                      <div className="detail-section print-hide">
                        <h3>Contact Information</h3>
                        <div className="detail-grid">
                          {person.contact.email && (
                            <div className="detail-item">
                              <strong>Email:</strong> {person.contact.email}
                            </div>
                          )}
                          {person.contact.phoneNumbers &&
                            person.contact.phoneNumbers.length > 0 && (
                              <div
                                className="detail-item"
                                style={{ gridColumn: "1 / -1" }}
                              >
                                <strong>Phone Numbers:</strong>
                                <ul
                                  style={{
                                    marginTop: "8px",
                                    paddingLeft: "20px",
                                  }}
                                >
                                  {person.contact.phoneNumbers.map(
                                    (phone: any, index: number) => (
                                      <li key={index}>
                                        {phone.phoneNumber} ({phone.network} -{" "}
                                        {phone.type})
                                      </li>
                                    )
                                  )}
                                </ul>
                              </div>
                            )}
                        </div>
                      </div>
                    )}

                    {person.addresses && person.addresses.length > 0 && (
                      <div className="detail-section print-hide">
                        <h3>Addresses</h3>
                        {person.addresses.map((address: any, index: number) => (
                          <div key={index} className="address-card">
                            <div className="address-type">{address.type}</div>
                            <div className="detail-grid">
                              {address.town && (
                                <div className="detail-item">
                                  <strong>Town:</strong> {address.town}
                                </div>
                              )}
                              {address.region && (
                                <div className="detail-item">
                                  <strong>Region:</strong> {address.region}
                                </div>
                              )}
                              {address.districtName && (
                                <div className="detail-item">
                                  <strong>District:</strong>{" "}
                                  {address.districtName}
                                </div>
                              )}
                              {address.community && (
                                <div className="detail-item">
                                  <strong>Community:</strong>{" "}
                                  {address.community}
                                </div>
                              )}
                              {address.postalCode && (
                                <div className="detail-item">
                                  <strong>Postal Code:</strong>{" "}
                                  {address.postalCode}
                                </div>
                              )}
                              {address.countryName && (
                                <div className="detail-item">
                                  <strong>Country:</strong>{" "}
                                  {address.countryName}
                                </div>
                              )}
                              {address.gpsAddressDetails && (
                                <div
                                  className="detail-item"
                                  style={{ gridColumn: "1 / -1" }}
                                >
                                  <strong>GPS Details:</strong>
                                  <div
                                    style={{
                                      marginTop: "8px",
                                      padding: "8px",
                                      background: "#f8f9fa",
                                      borderRadius: "4px",
                                    }}
                                  >
                                    {address.gpsAddressDetails.gpsName && (
                                      <div>
                                        <strong>GPS Name:</strong>{" "}
                                        {address.gpsAddressDetails.gpsName}
                                      </div>
                                    )}
                                    {address.gpsAddressDetails.street && (
                                      <div>
                                        <strong>Street:</strong>{" "}
                                        {address.gpsAddressDetails.street}
                                      </div>
                                    )}
                                    {address.gpsAddressDetails.latitude &&
                                      address.gpsAddressDetails.longitude && (
                                        <div>
                                          <strong>Coordinates:</strong>{" "}
                                          {address.gpsAddressDetails.latitude},{" "}
                                          {address.gpsAddressDetails.longitude}
                                        </div>
                                      )}
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {person.occupations && person.occupations.length > 0 && (
                      <div className="detail-section print-hide">
                        <h3>Occupations</h3>
                        <div className="detail-grid">
                          {person.occupations.map(
                            (occupation: any, index: number) => (
                              <div key={index} className="detail-item">
                                {occupation.name}
                              </div>
                            )
                          )}
                        </div>
                      </div>
                    )}

                    {transactionData && (
                      <div className="detail-section print-hide">
                        <h3>Transaction Information</h3>
                        <div className="detail-grid">
                          {transactionData.transactionGuid && (
                            <div className="detail-item">
                              <strong>Transaction GUID:</strong>
                              <div
                                style={{
                                  fontFamily: "monospace",
                                  fontSize: "0.9rem",
                                  marginTop: "4px",
                                }}
                              >
                                {transactionData.transactionGuid}
                              </div>
                            </div>
                          )}
                          {transactionData.shortGuid && (
                            <div className="detail-item">
                              <strong>Short GUID:</strong>{" "}
                              {transactionData.shortGuid}
                            </div>
                          )}
                          {transactionData.center && (
                            <div className="detail-item">
                              <strong>Center:</strong> {transactionData.center}
                            </div>
                          )}
                          {transactionData.source && (
                            <div className="detail-item">
                              <strong>Source:</strong> {transactionData.source}
                            </div>
                          )}
                          {transactionData.userID && (
                            <div className="detail-item">
                              <strong>User ID:</strong> {transactionData.userID}
                            </div>
                          )}
                          {transactionData.requestTimestamp && (
                            <div className="detail-item">
                              <strong>Request Timestamp:</strong>{" "}
                              {new Date(
                                transactionData.requestTimestamp
                              ).toLocaleString()}
                            </div>
                          )}
                          {transactionData.responseTimestamp && (
                            <div className="detail-item">
                              <strong>Response Timestamp:</strong>{" "}
                              {new Date(
                                transactionData.responseTimestamp
                              ).toLocaleString()}
                            </div>
                          )}
                          {selectedVerification.verification_result?.code && (
                            <div className="detail-item">
                              <strong>Verification Code:</strong>{" "}
                              {selectedVerification.verification_result.code}
                            </div>
                          )}
                          {selectedVerification.verification_result
                            ?.message && (
                            <div className="detail-item">
                              <strong>Message:</strong>{" "}
                              {selectedVerification.verification_result.message}
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    <div className="detail-section print-hide">
                      <h3>Verification Metadata</h3>
                      <div className="detail-grid">
                        <div className="detail-item">
                          <strong>Ghana Card Number:</strong>{" "}
                          {selectedVerification.ghana_card_number}
                        </div>
                        <div className="detail-item">
                          <strong>Date of Birth:</strong>{" "}
                          {selectedVerification.date_of_birth}
                        </div>
                        <div className="detail-item">
                          <strong>Verified At:</strong>{" "}
                          {new Date(
                            selectedVerification.created_at
                          ).toLocaleString()}
                        </div>
                        {selectedVerification.verification_type && (
                          <div className="detail-item">
                            <strong>Verification Type:</strong>{" "}
                            {selectedVerification.verification_type.toUpperCase()}
                          </div>
                        )}
                        {user?.role === "admin" &&
                          selectedVerification.creator_email && (
                            <div className="detail-item">
                              <strong>Link Created By:</strong>{" "}
                              {selectedVerification.creator_email}
                            </div>
                          )}
                      </div>
                    </div>
                  </div>
                );
              })()}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Verifications;
